import { useMemo } from "react";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { MultiPolygon, Polygon } from "geojson";

import {
  orderLayersByConfiguration,
  selectAllVersionLayers,
  useGetLayerFeatureTotalsQuery,
  useGetLayersQuery,
  useGetVersionConfigQuery,
} from "fond/api";
import { CommentLayerConfigIDs } from "fond/layers";
import {
  aggregateFeatureTotals,
  canFetchSublayerTotals,
  FeatureTotals,
  FeatureTotalsSummary,
  resolveLayerKeyVisibility,
} from "fond/map/featureTotalsSummary";
import useMapboxFeatureTotals from "fond/map/useMapboxFeatureTotals";
import useSublayerFeatureTotals from "fond/map/useSublayerFeatureTotals";
import { getCurrentProjectView } from "fond/project/redux";
import { getTypeCountByResolutionState } from "fond/redux/comments";
import { EnrichedMultiProject, Layer, Maybe, Project, Store } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { isVisible } from "fond/utils/configurations";
import { isMultiProject } from "fond/utils/project";

interface UseFeatureTotalsProps {
  project: Maybe<Project | EnrichedMultiProject>;
  versionId?: string;
  /**
   * When provided, feature totals only include features intersecting this geometry.
   * Features without a geometry are excluded.
   */
  selectionGeometry?: Polygon | MultiPolygon | null;
}

interface UseFeatureTotalsReturns {
  isLoadingFeatureTotals: boolean;
  loadLayerFeatureTotals: (layerConfig: LayerConfig) => void;
  featureTotals: FeatureTotals;
  /**
   * Aggregate totals for the current selection.
   */
  summaryTotals: FeatureTotalsSummary | undefined;
}

const EMPTY_LAYER_VIEW: { [key: string]: boolean } = {};

const useFeatureTotals = ({ versionId, project, selectionGeometry }: UseFeatureTotalsProps): UseFeatureTotalsReturns => {
  const skipProjectSpecificQueries = !versionId || isMultiProject(project);

  // We use `currentData` so that totals for the previous selection aren't shown while totals for a new selection
  // are still being fetched.
  const { currentData: layerFeatureTotals, isFetching: isFetchingFeatureTotals } = useGetLayerFeatureTotalsQuery(
    selectionGeometry ? { versionId, geometry: selectionGeometry } : { versionId },
    { skip: skipProjectSpecificQueries, refetchOnMountOrArgChange: true }
  );
  const { data: layers } = useGetLayersQuery(versionId ?? skipToken, { skip: skipProjectSpecificQueries });
  const commentLayerCount: FeatureTotals = useSelector((state: Store) =>
    getTypeCountByResolutionState(state)(state.comments.filters, selectionGeometry)
  );
  const layerConfigs = useSelector((state: Store) => selectAllVersionLayers(state, versionId));
  const layerView = useSelector((state: Store) => getCurrentProjectView(state.project)?.layers) ?? EMPTY_LAYER_VIEW;
  const { data: versionConfig } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });

  // Various parts of the frontend lie to each other about the existence of certain layers. The backend will get
  // upset if we try to get feature totals for layers that don't actually exist, so limit loading to only those
  // layers that appear plausibly real.
  const realLayers: Record<string, Layer> = useMemo(() => {
    if (!layers) return {};

    const layerConfigurationIds = Object.values(layers.entities)
      .filter((layer): layer is Layer => !!layer?.ID && !!layer?.LayerKey && !!layer?.Version && !!layer?.Configuration?.ID)
      .map((layer) => [layer.Configuration.ID, layer]);

    return Object.fromEntries(layerConfigurationIds);
  }, [layers]);

  const mapboxFeatureTotals = useMapboxFeatureTotals({ versionConfig, realLayers, layerConfigs, selectionGeometry });

  // `orderLayersByConfiguration` returns the layers the map draws, dropping those flagged as
  // hidden or belonging to a hidden group.
  const renderedIds = useMemo(() => new Set(versionConfig ? orderLayersByConfiguration(versionConfig).map(({ ID }) => ID) : []), [versionConfig]);

  const visibilityByLayerKey = useMemo(() => {
    if (!versionConfig) return {};

    return resolveLayerKeyVisibility(layerConfigs, {
      isEntityVisible: (id) => isVisible(versionConfig, { id, layerView }),
      isRendered: (id) => renderedIds.has(id),
      canBreakDown: (layer) => (!!selectionGeometry && canFetchSublayerTotals(layer)) || layer.Children.every((id) => id in mapboxFeatureTotals),
    });
  }, [versionConfig, layerConfigs, layerView, renderedIds, selectionGeometry, mapboxFeatureTotals]);

  const partialLayerConfigs = useMemo(
    () =>
      layerConfigs.filter(
        (layerConfig): layerConfig is LayerConfig =>
          layerConfig.Type === "LAYER" &&
          visibilityByLayerKey[layerConfig.Key ?? ""]?.status === "partial" &&
          // Visibility is resolved per layer key, and several configurations can share one, so only
          // those we can actually fetch a breakdown for are worth waiting on.
          canFetchSublayerTotals(layerConfig)
      ),
    [layerConfigs, visibilityByLayerKey]
  );

  const {
    totals: loadedSublayerTotals,
    loadLayerFeatureTotals,
    isFetching: isFetchingSublayerTotals,
    isLoadingSublayerTotals,
  } = useSublayerFeatureTotals({ versionId, selectionGeometry, partialLayerConfigs });

  // While totals are being fetched for new query args (e.g. a new selection), withhold all totals —
  // including those calculated client-side — so that every row shows as loading rather than a mix
  // of updated and stale counts. A background refetch of already-cached args doesn't withhold anything.
  const isLoadingNewTotals = isFetchingFeatureTotals && !layerFeatureTotals;

  // Assemble the feature totals from the various sources. This should roughly be in order from least
  // trustworthy to most trustworthy.
  const featureTotals = useMemo(
    () => (isLoadingNewTotals ? {} : { ...mapboxFeatureTotals, ...layerFeatureTotals, ...loadedSublayerTotals, ...commentLayerCount }),
    [isLoadingNewTotals, mapboxFeatureTotals, layerFeatureTotals, loadedSublayerTotals, commentLayerCount]
  );

  const geometryTypeByLayerKey = useMemo(() => {
    const result: Record<string, string | null> = {};
    layerConfigs.forEach(({ Key, GeometryType }) => {
      if (Key) result[Key] = GeometryType;
    });
    return result;
  }, [layerConfigs]);

  // Feature counts from the backend take preference over feature counts from the map.
  const sublayerTotals = useMemo(() => ({ ...mapboxFeatureTotals, ...loadedSublayerTotals }), [mapboxFeatureTotals, loadedSublayerTotals]);

  // Comments whose legend row (or the group containing it) is hidden are excluded, as for layers.
  // The comment types listed here all carry a geometry, so project comments (which have none, and
  // so can never fall within a selection) don't contribute.
  const commentCount = useMemo(
    () =>
      CommentLayerConfigIDs.reduce(
        (total, id) => (versionConfig && !isVisible(versionConfig, { id, layerView }) ? total : total + (commentLayerCount[id]?.count ?? 0)),
        0
      ),
    [commentLayerCount, versionConfig, layerView]
  );

  const summaryTotals = useMemo(() => {
    // Withhold the summary rather than briefly reporting a total that ignores the sublayers being hidden.
    if (!layerFeatureTotals || isLoadingSublayerTotals) return undefined;

    return aggregateFeatureTotals(layerFeatureTotals, { geometryTypeByLayerKey, visibilityByLayerKey, sublayerTotals, commentCount });
  }, [layerFeatureTotals, isLoadingSublayerTotals, geometryTypeByLayerKey, visibilityByLayerKey, sublayerTotals, commentCount]);

  const isLoadingFeatureTotals = isFetchingSublayerTotals || isFetchingFeatureTotals;

  return useMemo(
    () => ({ featureTotals, loadLayerFeatureTotals, isLoadingFeatureTotals, summaryTotals }),
    [featureTotals, loadLayerFeatureTotals, isLoadingFeatureTotals, summaryTotals]
  );
};

export default useFeatureTotals;
